// src/App.tsx
// Updated to use unified Home page + DisclaimerSheet + new Login/Signup.
// All other routes and auth logic remain unchanged from your original.

/* eslint-disable @typescript-eslint/no-unused-vars */
import { BrowserRouter as Router, Routes, Route, useLocation, matchPath } from "react-router-dom";

// Pages
import Home           from "./pages/Home";
import Gungrahan      from "./pages/Gungrahan";
import Report         from "./pages/Report";
import DailyTask      from "./pages/DailyTask";
import Navbar         from "./components/Navbar";
import Pravachan      from "./pages/Pravachan";
import PravachanReport from "./pages/PravachanReport";
import Login          from "./pages/Login";
import Signup         from "./pages/Signup";
import HowItWorks     from "./pages/HowItWorks";
import Ashirwad       from "./pages/Ashirwad";
import AshirwadReport from "./pages/AshirwadReport";
import ComingSoon     from "./pages/Soon";
import Brahmnasange   from "./pages/BrahmnaSange";
import BrahmnaSangeReport from "./pages/BrahmnaSangeReport";
import SmrutiGallery  from "./pages/SmrutiGallery";
import SatsangTodo    from "./pages/SatsangTodo";
import SmrutiStorage  from "./pages/SmrutiStorage";
import PublicFeed     from "./pages/PublicFeed";
import SharedPravachan from "./pages/SharedPravachan";
import Settings       from "./pages/Settings";

// NEW: HomeAfterLogin is replaced — use the /welcome alias below.
// The unified Home page renders the dashboard grid when the user is logged in.
// Keep a thin HomeAfterLogin shim that just navigates to "/" for any old links.
import { AuthProvider } from "./contexts/AuthContext";
import PrivateRoute    from "./components/PrivateRoute";

// One-time disclaimer bottom sheet — shown automatically on first visit
import DisclaimerSheet from "./components/Disclaimersheet";

// ── Pages with full clean UI (no bottom margin from Navbar) ──
const FULL_WIDTH_PAGES = ["/", "/login", "/signup", "/how-it-works"];
const HIDE_NAVBAR_PATTERNS = ["/shared-pravachan/:userId/:timestamp"];

function LayoutWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const hideNavbar = HIDE_NAVBAR_PATTERNS.some(pattern =>
    matchPath(pattern, location.pathname)
  );

  // Home, login, signup handle their own layout entirely
  const isFullWidth = FULL_WIDTH_PAGES.includes(location.pathname);

  return (
    <div className={`min-h-screen ${isFullWidth ? "bg-white" : "bg-gray-50"}`}>
      {/* DisclaimerSheet is mounted here so it appears on the very first page load */}
      <DisclaimerSheet />

      {!hideNavbar && !isFullWidth && <Navbar />}

      <main className={isFullWidth ? "" : "mb-20"}>
        {children}
      </main>
    </div>
  );
}

// Thin shim — if anyone navigates to /welcome directly while logged out,
// PrivateRoute will redirect them to login anyway.
function HomeAfterLoginShim() {
  // The unified Home page now shows the dashboard grid when logged in.
  // You can keep this shim pointing to "/" or render Home directly.
  return <Home />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <LayoutWrapper>
          <Routes>
            {/* ── Public Routes ──────────────────────────────────────── */}
            <Route path="/"              element={<Home />} />
            <Route path="/login"         element={<Login />} />
            <Route path="/signup"        element={<Signup />} />
            <Route path="/how-it-works"  element={<HowItWorks />} />
            <Route path="/public-feed"   element={<PublicFeed />} />
            <Route
              path="/shared-pravachan/:userId/:timestamp"
              element={<SharedPravachan />}
            />

            {/* ── Protected Routes ────────────────────────────────────── */}
            {/* /welcome shows the unified Home which renders the dashboard when logged in */}
            <Route path="/welcome" element={
              <PrivateRoute><HomeAfterLoginShim /></PrivateRoute>
            } />

            <Route path="/gungrahan"          element={<PrivateRoute><Gungrahan /></PrivateRoute>} />
            <Route path="/report"             element={<PrivateRoute><Report /></PrivateRoute>} />
            <Route path="/daily-task"         element={<PrivateRoute><DailyTask /></PrivateRoute>} />
            <Route path="/pravachan"          element={<PrivateRoute><Pravachan /></PrivateRoute>} />
            <Route path="/pravachan-report"   element={<PrivateRoute><PravachanReport /></PrivateRoute>} />
            <Route path="/Ashirwad"           element={<PrivateRoute><Ashirwad /></PrivateRoute>} />
            <Route path="/Ashirwad-report"    element={<PrivateRoute><AshirwadReport /></PrivateRoute>} />
            <Route path="/Soon"               element={<PrivateRoute><ComingSoon /></PrivateRoute>} />
            <Route path="/satsang-todo"       element={<PrivateRoute><SatsangTodo /></PrivateRoute>} />
            <Route path="/smruti-storage"     element={<PrivateRoute><SmrutiStorage /></PrivateRoute>} />
            <Route path="/Smruti-Gallery"     element={<PrivateRoute><SmrutiGallery /></PrivateRoute>} />
            <Route path="/Brahmnasange"       element={<PrivateRoute><Brahmnasange /></PrivateRoute>} />
            <Route path="/BrahmnaSangeReport" element={<PrivateRoute><BrahmnaSangeReport /></PrivateRoute>} />
            <Route path="/Settings"           element={<PrivateRoute><Settings /></PrivateRoute>} />
          </Routes>
        </LayoutWrapper>
      </AuthProvider>
    </Router>
  );
}

export default App;